/**
 * RedemptionSuccessFeedback Component
 * Redemption status and account actions
 * Shows granted entitlements and badges
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../lib/firebase';
import type { RedeemCodeResponse } from '../../lib/types/redeemGroup';

interface RedemptionSuccessFeedbackProps {
  result: RedeemCodeResponse;
  onClose: () => void;
}

export function RedemptionSuccessFeedback({
  result,
  onClose,
}: RedemptionSuccessFeedbackProps) {
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  /**
   * Result of the last "Check for access" press.
   *
   * `'active'` means the claims have since landed, so the panel switches to the
   * live state — the whole point of offering the button. Without this the
   * button refreshed the token and changed nothing on screen, so a user could
   * press it forever with no way to tell whether it had worked.
   */
  const [checkResult, setCheckResult] = useState<
    'idle' | 'active' | 'still-pending' | 'error'
  >('idle');

  // The grant is stored but not yet in the user's Auth claims, so it confers no
  // access. The redemption itself succeeded and the code is spent, so this is a
  // degraded success, not a failure — but the panel must not claim delivery it
  // cannot see. "Items Granted" is for the live case only.
  //
  // `!== true`, not `=== false`: a response that omits the field entirely means
  // "unknown", which must not claim delivery. The field is required by the
  // API contract, so this only bites on a version skew — where a muted panel
  // is the safe way to be wrong.
  //
  // A successful re-check clears it: `result` is a prop fixed at mutation time
  // and cannot know that a later sync landed.
  const pending =
    result.entitlementsActive !== true && checkResult !== 'active';

  const handleContinueToDashboard = () => {
    navigate('/dashboard');
  };

  // Re-mint the ID token and REPORT what it now says.
  //
  // Only offered on the pending path. The hook deliberately skips the refresh
  // at mutation time when the backend says the claims are not written — at that
  // instant re-minting can only reproduce the same stale claims. This runs
  // later, which is the whole difference: by now a retry, an operator resync or
  // an unrelated event may have written them, and a fresh token is the only way
  // to find out. If it has not happened yet, say so rather than leaving the
  // spinner to imply something changed.
  const handleRefreshAccess = async () => {
    setIsRefreshing(true);
    try {
      const token = await auth.currentUser?.getIdTokenResult(true);
      const claims = token?.claims?.entitlements;
      const granted = Array.isArray(claims) ? (claims as string[]) : [];
      const allPresent =
        result.entitlements.length > 0 &&
        result.entitlements.every(e => granted.includes(e));
      setCheckResult(allPresent ? 'active' : 'still-pending');
    } catch (error) {
      console.error('Token refresh failed:', error);
      setCheckResult('error');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="mb-8 relative">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center relative">
        {/* Status Icon: tick when access is live, clock while it is being applied */}
        <div className="mb-6">
          <div
            className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full ${
              pending
                ? 'bg-amber-100 dark:bg-amber-900/20'
                : 'bg-green-100 dark:bg-green-900/20'
            }`}
          >
            <svg
              className={`h-8 w-8 ${
                pending
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-green-600 dark:text-green-400'
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={
                  pending
                    ? 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                    : 'M5 13l4 4L19 7'
                }
              />
            </svg>
          </div>
        </div>

        {/* Status Message */}
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {pending ? 'Code Redeemed' : 'Code Redeemed Successfully!'}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Your code{' '}
          <span className="font-mono font-semibold">{result.code}</span> has
          been redeemed.
          {pending &&
            ' Your access is still being applied — it should appear shortly.'}
        </p>

        {/* Outcome of a "Check for access" press. A live region, because the
            only thing that changes on screen is this line. */}
        {pending && checkResult !== 'idle' && (
          <p
            role="status"
            aria-live="polite"
            className="text-sm text-gray-500 dark:text-gray-400 mb-6 -mt-4"
          >
            {checkResult === 'still-pending' &&
              'Not there yet. Nothing is lost — your code is redeemed and the access is on its way.'}
            {checkResult === 'error' &&
              'Could not check just now. Try again in a moment.'}
          </p>
        )}

        {/* Granted Items */}
        {(result.entitlements.length > 0 || result.badges.length > 0) && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {pending ? 'Items Being Applied' : 'Items Granted'}
            </h3>
            <div className="space-y-3 text-left">
              {/* Entitlements */}
              {result.entitlements.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Entitlements:
                  </p>
                  <ul className="space-y-1">
                    {result.entitlements.map(entitlement => (
                      <li
                        key={entitlement}
                        className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                      >
                        <svg
                          className="w-4 h-4 text-green-500 mr-2"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {entitlement}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Badges */}
              {result.badges.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Badges:
                  </p>
                  <ul className="space-y-1">
                    {result.badges.map(badge => (
                      <li
                        key={badge}
                        className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                      >
                        <svg
                          className="w-4 h-4 text-green-500 mr-2"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {badge}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {pending && (
            <button
              onClick={handleRefreshAccess}
              disabled={isRefreshing}
              aria-busy={isRefreshing}
              className="btn-primary"
            >
              {isRefreshing ? 'Checking…' : 'Check for access'}
            </button>
          )}
          <button
            onClick={handleContinueToDashboard}
            className={pending ? 'btn-secondary' : 'btn-primary'}
          >
            Continue to Dashboard
          </button>
          <button onClick={onClose} className="btn-secondary">
            Redeem Another Code
          </button>
        </div>
      </div>
    </div>
  );
}
